/**
 * TEMPORARY operator widget — two panes over a SHARED selected user:
 *
 *  LEFT  — settle a partial cash/card amount, with the amount computed from a
 *          checklist of the user's RECENT ARTICLE PURCHASES (a calculator; the
 *          picked items go into the settlement comment as a human-readable
 *          reference until the structured article-link standard lands).
 *  RIGHT — browse ALL articles in a category tree and BUY one for the user with
 *          one click + Undo (see ArticleBuyPane).
 *
 * The user is chosen via a persistent BOOKMARK bar (search → a quick-select
 * button; +/pencil/− manage the bookmarks). Both panes + the bookmarks persist
 * to localStorage.
 *
 * Settle reuses the existing flow verbatim (adopt user_identity.id → POST
 * /settlements; direction derived server-side from the balance sign). Buy/undo
 * go through the thin /users/{sid}/buy + DELETE pass-throughs.
 *
 * Remove later: delete this file + ArticleBuyPane.tsx + the single
 * <PurchaseSettleWidget/> line in FinanceCashPage.tsx.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { DndProvider, useDrag, useDrop } from "react-dnd";
import { MultiBackend } from "react-dnd-multi-backend";
import { HTML5toTouch } from "rdndmb-html5-to-touch";
import {
  Alert,
  Button,
  Card,
  Col,
  Grid,
  Input,
  Modal,
  Popconfirm,
  Row,
  Select,
  Space,
  Spin,
  Table,
  Typography,
  message,
} from "antd";
import {
  CheckCircleFilled,
  CloseCircleFilled,
  CloseOutlined,
  EditOutlined,
  HolderOutlined,
  MinusOutlined,
  PlusOutlined,
  QuestionCircleFilled,
  WarningFilled,
} from "@ant-design/icons";
import type { ColumnsType } from "antd/es/table";
import dayjs from "dayjs";

import {
  ApiError,
  type PaymentMethodRow,
  type TransactionRow,
  type UserRow,
  api,
} from "../../api/client";
import { ArticleBuyPane } from "./ArticleBuyPane";
import { SumupReaderSettle } from "../../components/SumupReaderSettle";
import { parseTxComment, TX_VERB_META } from "../../utils/txComment";
import { NumInput } from "../../components/NumInput";

const { Text, Title } = Typography;

const LS_SEL = "cashPurchaseSettleUserSid";
const LS_BOOKMARKS = "cashPurchaseBookmarks";
const RECENT_LIMIT = 40;
// Card/SumUp floor — a settlement can't be less than €1.00.
const MIN_CHARGE_CENTS = 100;

const fmtCents = (c: number | null | undefined): string =>
  c === null || c === undefined
    ? "—"
    : new Intl.NumberFormat("de-DE", {
        style: "currency",
        currency: "EUR",
      }).format(c / 100);

// Only article PURCHASES are settleable + summed into the amount.
const isArticleBuy = (t: TransactionRow) =>
  t.article_id != null && t.article_id > 0 && t.amount < 0;

// Rich "what was this" cell — payment method (cash 💵 / card 💳), voucher 🎟️,
// corrections, article buys, deposits — like the patched kiosk history. Reuses
// the shared comment-tag parser (settle's /modifier carries the method).
function renderTxDetail(row: TransactionRow) {
  const tag = parseTxComment(row.comment);
  if (tag) {
    const meta = (
      TX_VERB_META as Record<string, { label: string; color: string } | undefined>
    )[tag.verb];
    let emoji = "🏷️";
    if (tag.verb === "voucher" || tag.verb === "voucher_sweep") emoji = "🎟️";
    else if (tag.verb === "settle")
      emoji =
        tag.modifier === "cash" ? "💵" : tag.modifier === "card" ? "💳" : "💶";
    else if (tag.verb === "correct") emoji = "🔧";
    return (
      <span>
        {emoji} {meta?.label ?? tag.verb}
        {tag.modifier ? ` · ${tag.modifier}` : ""}
      </span>
    );
  }
  if (isArticleBuy(row)) {
    return (
      <span>
        🛒 {row.quantity && row.quantity > 1 ? `${row.quantity}× ` : ""}
        {row.article_name ?? "article"}
      </span>
    );
  }
  if (row.recipient_transaction_id != null || row.sender_transaction_id != null) {
    return <span>↔️ transfer</span>;
  }
  return <span>{row.amount >= 0 ? "💶 deposit" : "💶 charge"}</span>;
}

type Method = "cash" | "card";
// State machine for the windowed cash-settle workflow.
type CashFlow =
  | { phase: "confirm"; amount: number; isTopUp: boolean; before: number }
  | { phase: "processing"; amount: number; isTopUp: boolean; before: number }
  | { phase: "success"; amount: number; before: number; after: number }
  | { phase: "error"; amount: number; title: string; detail: string };
interface Bookmark {
  sid: number;
  label: string;
}

function loadBookmarks(): Bookmark[] {
  try {
    const raw = localStorage.getItem(LS_BOOKMARKS);
    const arr = raw ? (JSON.parse(raw) as unknown) : [];
    if (!Array.isArray(arr)) return [];
    return arr.filter(
      (b): b is Bookmark =>
        typeof b === "object" &&
        b !== null &&
        typeof (b as Bookmark).sid === "number" &&
        typeof (b as Bookmark).label === "string",
    );
  } catch {
    return [];
  }
}

// Turn a settlement API failure into operator-readable copy. The 409 boundary
// errors carry a structured body ({error, current_bank, after, limit,
// remediation}) nested under `detail`; translate the known codes into plain
// language and fall back to the raw detail for anything else.
function humanizeSettleError(e: unknown): { title: string; detail: string } {
  if (e instanceof ApiError) {
    const body = e.body as { detail?: Record<string, unknown> } | null;
    const d = body && typeof body.detail === "object" ? body.detail : null;
    const after = typeof d?.after === "number" ? (d.after as number) : null;
    const limit = typeof d?.limit === "number" ? (d.limit as number) : null;
    switch (e.errorCode) {
      case "boundary_account_lower":
        return {
          title: "The cash drawer can't go that low",
          detail:
            `This payment would push the drawer/bank balance to ${fmtCents(
              after,
            )}, below the allowed floor of ${fmtCents(limit)}. ` +
            "Lower the amount, settle other accounts into the drawer first, " +
            "or raise the account lower-bound limit in the strichliste settings.",
        };
      case "boundary_account_upper":
        return {
          title: "The cash drawer is too full",
          detail:
            `This payout would push the drawer/bank balance to ${fmtCents(
              after,
            )}, above the allowed ceiling of ${fmtCents(limit)}. ` +
            "Settle the bank user first, or raise the account upper-bound limit.",
        };
      case "boundary_payment_lower":
        return {
          title: "Amount too large for one payment",
          detail:
            "This amount is beyond the per-payment limit — split it into " +
            "smaller settlements, or raise payment.boundary.lower.",
        };
      default:
        return { title: "Payment failed", detail: e.detail };
    }
  }
  if (e instanceof Error) return { title: "Payment failed", detail: e.message };
  return { title: "Payment failed", detail: String(e) };
}

// "Everything worked" screen — a big green check + a before → +added → after
// balance flow. Shared by the cash window and the card success view.
function SettleSuccessView({
  before,
  added,
  after,
  method,
  onDone,
}: {
  before: number;
  added: number;
  after: number;
  method: Method;
  onDone: () => void;
}) {
  return (
    <div style={{ textAlign: "center", padding: "24px 8px" }}>
      <CheckCircleFilled style={{ color: "#52c41a", fontSize: 88 }} />
      <Title level={3} style={{ marginTop: 16, marginBottom: 20 }}>
        {method === "cash" ? "Cash received" : "Card payment received"}
      </Title>
      <div
        style={{
          display: "flex",
          alignItems: "baseline",
          justifyContent: "center",
          gap: 10,
          flexWrap: "wrap",
          marginBottom: 24,
        }}
      >
        <Text type="secondary" style={{ fontSize: 18 }}>
          {fmtCents(before)}
        </Text>
        <Text type="secondary">→</Text>
        <Text strong style={{ color: "#52c41a", fontSize: 20 }}>
          +{fmtCents(added)}
        </Text>
        <Text type="secondary">→</Text>
        <Text strong style={{ fontSize: 26 }}>
          {fmtCents(after)}
        </Text>
      </div>
      <Button type="primary" size="large" block onClick={onDone}>
        Done
      </Button>
    </div>
  );
}

// A single draggable bookmark chip — drag the ⠿ handle to reorder (react-dnd
// multi-backend → works with mouse AND touch). The select / edit / delete
// buttons keep their own clicks.
const BOOKMARK_DND = "cash-bookmark";
function BookmarkChip({
  index,
  bookmark,
  active,
  onSelect,
  onEdit,
  onDelete,
  onMove,
}: {
  index: number;
  bookmark: Bookmark;
  active: boolean;
  onSelect: (sid: number) => void;
  onEdit: (index: number) => void;
  onDelete: (index: number) => void;
  onMove: (from: number, to: number) => void;
}) {
  const ref = useRef<HTMLDivElement>(null);
  const [, drop] = useDrop<{ index: number }>({
    accept: BOOKMARK_DND,
    hover(item) {
      if (item.index === index) return;
      onMove(item.index, index);
      item.index = index; // keep the dragged item's index in sync mid-hover
    },
  });
  const [{ isDragging }, drag] = useDrag({
    type: BOOKMARK_DND,
    item: { index },
    collect: (m) => ({ isDragging: m.isDragging() }),
  });
  // Whole chip is the drag source + drop target (mirrors the catalog/managed-
  // list pattern); the ⠿ glyph is a visual affordance.
  drag(drop(ref));
  return (
    <div ref={ref} style={{ opacity: isDragging ? 0.4 : 1 }}>
      <Space.Compact size="large">
        <span
          title="Drag to reorder"
          style={{
            display: "inline-flex",
            alignItems: "center",
            paddingInline: 7,
            cursor: "grab",
            color: "#69b1ff",
            border: "1px solid #69b1ff",
            borderRight: 0,
            borderRadius: "8px 0 0 8px",
            background: "#e6f4ff",
          }}
        >
          <HolderOutlined />
        </span>
        <Button
          style={{
            fontWeight: 600,
            background: active ? "#91caff" : "#e6f4ff",
            borderColor: "#69b1ff",
            color: "#0958d9",
          }}
          onClick={() => onSelect(bookmark.sid)}
        >
          {bookmark.label}
        </Button>
        <Button
          icon={<EditOutlined />}
          title="Change the bookmarked user"
          onClick={() => onEdit(index)}
        />
        <Button
          icon={<MinusOutlined />}
          danger
          title="Remove bookmark"
          onClick={() => onDelete(index)}
        />
      </Space.Compact>
    </div>
  );
}

export function PurchaseSettleWidget({
  onSettled,
}: {
  onSettled?: () => void;
}) {
  // --- shared selected user ---
  const [bookmarks, setBookmarks] = useState<Bookmark[]>(loadBookmarks);
  const [selSid, setSelSid] = useState<number | null>(() => {
    const raw = localStorage.getItem(LS_SEL);
    const n = raw ? Number(raw) : NaN;
    return Number.isFinite(n) ? n : null;
  });
  const [selUser, setSelUser] = useState<UserRow | null>(null);
  // Voucher status of the selected user (from /public/guests) → drives the
  // "Convert to Guest" button for a non-convertible voucher holder.
  const [voucherInfo, setVoucherInfo] = useState<{
    isVoucher: boolean;
    allowConvert: boolean;
  } | null>(null);
  const [convertOpen, setConvertOpen] = useState(false);
  const [convertBusy, setConvertBusy] = useState(false);
  const [convertName, setConvertName] = useState("");
  const [convertRealName, setConvertRealName] = useState("");
  const [convertPublicNote, setConvertPublicNote] = useState("");
  const [convertInternalNote, setConvertInternalNote] = useState("");
  const [operatorUsername, setOperatorUsername] = useState("operator");
  // Custom amount override (cents). When set, it replaces the checked-purchase
  // sum; checking a purchase clears it (back to the calculated sum).
  const [customAmount, setCustomAmount] = useState<number | null>(null);
  const [customOpen, setCustomOpen] = useState(false);
  const [customInput, setCustomInput] = useState<number | null>(null);
  // For a NO-DEBT user, the custom-amount entry is gated behind an explicit
  // "this is a top-up" confirmation — the input only appears once it's passed.
  const [customGatePassed, setCustomGatePassed] = useState(true);

  // --- left pane (settle) ---
  const [methods, setMethods] = useState<PaymentMethodRow[]>([]);
  const [tx, setTx] = useState<TransactionRow[]>([]);
  const [txLoading, setTxLoading] = useState(false);
  const [selectedTxIds, setSelectedTxIds] = useState<number[]>([]);
  const [settling, setSettling] = useState(false);
  const [refreshTick, setRefreshTick] = useState(0);
  const [pendingSelect, setPendingSelect] = useState<number | null>(null);
  const [cardModalOpen, setCardModalOpen] = useState(false);
  const [cardAmount, setCardAmount] = useState<number | null>(null);
  // Balance captured when the card flow opened → drives the card success screen
  // (before → +amount → after), mirroring the cash window.
  const [cardBalanceBefore, setCardBalanceBefore] = useState<number | null>(
    null,
  );
  const [cardSuccess, setCardSuccess] = useState<{
    before: number;
    added: number;
    after: number;
  } | null>(null);
  // Windowed CASH workflow — confirm → processing → success / error.
  const [cashFlow, setCashFlow] = useState<CashFlow | null>(null);
  const [msgApi, msgHolder] = message.useMessage();
  const screens = Grid.useBreakpoint();
  const isMobile = !screens.md; // collapse the card padding cascade on phones
  const [error, setError] = useState<string | null>(null);

  // --- user picker (add/edit bookmark) ---
  const [pickerOpen, setPickerOpen] = useState(false);
  const [pickerEditIndex, setPickerEditIndex] = useState<number | null>(null);
  const [users, setUsers] = useState<UserRow[]>([]);
  const [userQuery, setUserQuery] = useState("");
  const [usersLoading, setUsersLoading] = useState(false);
  // Whether SumUp is turned on (Settings → SumUp). `null` = still loading; the
  // CARD button is hidden only once we KNOW it's off, so it doesn't flicker on a
  // SumUp-enabled venue.
  const [sumupEnabled, setSumupEnabled] = useState<boolean | null>(null);

  useEffect(() => {
    void (async () => {
      try {
        setMethods(await api.listPaymentMethods(false));
      } catch (e) {
        if (e instanceof ApiError) setError(e.message);
      }
    })();
    // CARD is only meaningful when SumUp is enabled; gate the button on it.
    void api
      .getSumupConfig()
      .then((c) => setSumupEnabled(Boolean(c.enabled)))
      .catch(() => setSumupEnabled(false));
  }, []);

  useEffect(() => {
    if (selSid === null) localStorage.removeItem(LS_SEL);
    else localStorage.setItem(LS_SEL, String(selSid));
  }, [selSid]);

  useEffect(() => {
    localStorage.setItem(LS_BOOKMARKS, JSON.stringify(bookmarks));
  }, [bookmarks]);

  // The logged-in operator — stamped into the convert audit note.
  useEffect(() => {
    void api
      .me()
      .then((m) => {
        if (m.username) setOperatorUsername(m.username);
      })
      .catch(() => {});
  }, []);

  // (Re)load the selected user's live balance + recent transactions.
  useEffect(() => {
    if (selSid === null) {
      setSelUser(null);
      setTx([]);
      setSelectedTxIds([]);
      setVoucherInfo(null);
      return;
    }
    let cancelled = false;
    setTxLoading(true);
    void (async () => {
      try {
        const [u, t, guests] = await Promise.all([
          api.getUserByStrichliste(selSid),
          api.listUserTransactions(selSid),
          api.getPublicGuests().catch(() => null),
        ]);
        if (cancelled) return;
        setSelUser(u);
        setTx(t.items);
        const key = String(selSid);
        setVoucherInfo({
          isVoucher: guests?.user_types?.[key] === "voucher",
          allowConvert: guests?.voucher_flags?.[key]?.allow_convert ?? true,
        });
      } catch (e) {
        if (!cancelled && e instanceof ApiError) setError(e.message);
      } finally {
        if (!cancelled) setTxLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [selSid, refreshTick]);

  // Clear the purchase selection when the USER changes — but NOT on a buy/settle
  // refresh of the same user (so a buy can auto-check its row, below).
  useEffect(() => {
    setSelectedTxIds([]);
  }, [selSid]);

  // After a buy refreshes the history, auto-check the just-bought article.
  useEffect(() => {
    if (pendingSelect === null) return;
    if (tx.some((t) => t.id === pendingSelect)) {
      setSelectedTxIds((prev) =>
        prev.includes(pendingSelect) ? prev : [...prev, pendingSelect],
      );
      setPendingSelect(null);
    }
  }, [tx, pendingSelect]);

  // Debounced server-side user search (endpoint caps page_size at 500), shared
  // by the always-present picker + the bookmark add/edit modal.
  useEffect(() => {
    let cancelled = false;
    setUsersLoading(true);
    const timer = setTimeout(() => {
      void (async () => {
        try {
          const searching = userQuery.trim().length > 0;
          const up = await api.listUsers({
            q: searching ? userQuery.trim() : undefined,
            // Vouchers are hidden from the default list but searchable. The
            // default list needs the full set so we can filter it client-side;
            // omitting `sort` keeps the backend's guests-first a-z default.
            show_vouchers: searching,
            page_size: searching ? 200 : 500,
          });
          if (!cancelled) setUsers(up.items.filter((u) => u.user_type !== "bank"));
        } catch (e) {
          if (!cancelled && e instanceof ApiError) setError(e.message);
        } finally {
          if (!cancelled) setUsersLoading(false);
        }
      })();
    }, 250);
    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [userQuery]);

  const userOptions = useMemo(() => {
    const searching = userQuery.trim().length > 0;
    const label = (u: UserRow) =>
      `${u.name}${u.real_name ? ` (${u.real_name})` : ""}${
        u.disabled ? " (disabled)" : ""
      } — ${fmtCents(u.balance_cents)}`;
    const opts = users
      // Default list: only ACTIONABLE users — disabled guests + anyone not
      // settled at €0. Searching reveals everyone (incl. vouchers + €0 users).
      // Order stays guests-first a-z (the /users default sort).
      .filter(
        (u) =>
          searching ||
          (u.disabled && u.user_type === "guest") ||
          (u.balance_cents != null && u.balance_cents !== 0),
      )
      // Drop the selected user from the mapped list — its option is unshifted
      // below from the LIVE `selUser` (refreshed after every settle/top-up), so
      // the picker never shows a stale balance from an older search page.
      .filter((u) => u.strichliste_user_id !== selSid)
      .map((u) => ({
        value: u.strichliste_user_id,
        label: label(u),
        name: u.name,
      }));
    // Always render the current selection from the fresh `selUser` so its
    // balance is up to date and it stays visible even when filtered out.
    if (selSid !== null && selUser) {
      opts.unshift({ value: selSid, label: label(selUser), name: selUser.name });
    }
    return opts;
  }, [users, selSid, selUser, userQuery]);

  // ---- bookmark management ----
  const openPicker = (editIndex: number | null) => {
    setPickerEditIndex(editIndex);
    setUserQuery("");
    setUsers([]);
    setPickerOpen(true);
  };

  const pickUser = (sid: number, name?: string) => {
    // Prefer the name the operator just saw in the option; fall back to the
    // loaded list / the current selection, only then to a bare id.
    const label =
      name ??
      users.find((x) => x.strichliste_user_id === sid)?.name ??
      (selUser?.strichliste_user_id === sid ? selUser.name : undefined) ??
      `#${sid}`;
    setBookmarks((prev) => {
      const next = [...prev];
      if (pickerEditIndex !== null && pickerEditIndex < next.length) {
        next[pickerEditIndex] = { sid, label };
      } else if (!next.some((b) => b.sid === sid)) {
        next.push({ sid, label });
      }
      return next;
    });
    setSelSid(sid);
    setPickerOpen(false);
    setUserQuery("");
  };

  const deleteBookmark = (index: number) => {
    setBookmarks((prev) => {
      const removed = prev[index];
      if (removed && removed.sid === selSid) setSelSid(null);
      return prev.filter((_, i) => i !== index);
    });
  };

  // Pin the CURRENTLY selected user directly — no picker. (The picker stays for
  // the pencil/edit action.)
  const bookmarkCurrent = () => {
    if (selSid === null || selUser === null) return;
    setBookmarks((prev) =>
      prev.some((b) => b.sid === selSid)
        ? prev
        : [...prev, { sid: selSid, label: selUser.name }],
    );
  };

  // Drag-to-reorder (called on hover by BookmarkChip). Persists via the
  // bookmarks→localStorage effect.
  const moveBookmark = useCallback((from: number, to: number) => {
    setBookmarks((prev) => {
      if (
        from === to ||
        from < 0 ||
        to < 0 ||
        from >= prev.length ||
        to >= prev.length
      )
        return prev;
      const next = [...prev];
      const [moved] = next.splice(from, 1);
      next.splice(to, 0, moved);
      return next;
    });
  }, []);

  // Custom amount resets per user; and the moment the operator (re)checks a
  // purchase it reverts to the checked-sum (the "start from 0 + marked items"
  // behaviour — the checklist is for calculation only).
  useEffect(() => {
    setCustomAmount(null);
  }, [selSid]);
  useEffect(() => {
    if (selectedTxIds.length > 0) setCustomAmount(null);
  }, [selectedTxIds]);

  // ---- left pane (settle) derived ----
  // Full recent history (newest-first from the API), sliced for the table.
  const recentTx = useMemo(() => tx.slice(0, RECENT_LIMIT), [tx]);
  const selectedSum = useMemo(
    () =>
      recentTx
        .filter((t) => isArticleBuy(t) && selectedTxIds.includes(t.id))
        .reduce((s, t) => s + Math.abs(t.amount), 0),
    [recentTx, selectedTxIds],
  );
  const balance = selUser?.balance_cents ?? null;
  const absDebt = balance !== null && balance < 0 ? -balance : 0;
  const cashAvailable = methods.some((m) => m.code === "cash" && m.active);
  // The amount to settle: a custom override (if set) else the sum of the checked
  // purchases. A CUSTOM amount may EXCEED the debt → a top-up into credit
  // (confirmed at settle; cash only). The checked-purchase sum stays capped to
  // what's owed. Cash has no minimum; card needs €1.
  const settleAmount =
    customAmount !== null ? customAmount : Math.min(selectedSum, absDebt);
  // A custom amount beyond the debt is a top-up (cash collected into credit).
  // At €0 / in credit there is no debt, so ANY custom amount is a pure top-up —
  // it moves money exactly like a top-up from a debt (user → bank).
  const isTopUp = customAmount !== null && customAmount > absDebt;
  const capped = customAmount === null && selectedSum > absDebt && absDebt > 0;
  // Cash has NO minimum — it works for any amount > 0. Only card (SumUp) needs €1.
  const belowCardMin = settleAmount > 0 && settleAmount < MIN_CHARGE_CENTS;
  // Settle/top-up needs a positive amount — which, at €0 / in credit, only a
  // CUSTOM amount can produce (the checked-purchase sum is capped to the debt).
  // So the "gate" for a non-debtor is simply entering a custom value.
  const canSettleBase =
    selUser !== null &&
    balance !== null &&
    settleAmount > 0 &&
    !settling &&
    !txLoading;

  // Core settle execution, shared by the windowed cash flow. Creates the admin
  // row on the fly if needed and posts the settlement; THROWS on failure so the
  // caller can drive the success/error screen.
  const performSettle = async (amount: number, m: Method) => {
    if (selSid === null || selUser === null) throw new Error("No user selected.");
    const methodId = methods.find((x) => x.code === m && x.active)?.id ?? null;
    if (methodId === null) {
      throw new Error(
        `'${m}' payment method not found — add one in Settings → Payment methods.`,
      );
    }
    let userIdentityId = selUser.id;
    if (userIdentityId === null) {
      const created = await api.createUser({
        strichliste_user_id: selSid,
        user_type: "guest",
        name: selUser.name,
      });
      if (created.id === null) {
        throw new Error(
          "Admin row for the user could not be created — settlement aborted.",
        );
      }
      userIdentityId = created.id;
    }
    const chosen = recentTx.filter(
      (t) => isArticleBuy(t) && selectedTxIds.includes(t.id),
    );
    const topUp = amount > absDebt;
    const comment = (
      customAmount !== null
        ? `${topUp ? "Top-up" : "Custom amount"} (temp): ${fmtCents(amount)}`
        : "Purchase-settle (temp): " +
          chosen
            .map(
              (t) =>
                `${t.quantity && t.quantity > 1 ? `${t.quantity}× ` : ""}${
                  t.article_name ?? "article"
                }`,
            )
            .join(", ")
    ).slice(0, 250);
    await api.createSettlement({
      user_id: userIdentityId,
      payment_method_id: methodId,
      amount_cents: amount,
      comment,
      allow_topup: topUp,
    });
  };

  // Open the windowed cash confirmation ("Did they hand over the cash?").
  const openCashConfirm = () => {
    if (selSid === null || selUser === null || !canSettleBase || balance === null)
      return;
    const amount = settleAmount;
    if (amount <= 0) return; // cash has no minimum
    setError(null);
    setCashFlow({
      phase: "confirm",
      amount,
      isTopUp: amount > absDebt,
      before: balance,
    });
  };

  // "Yes, received" → post the settlement, then show the success (balance flow)
  // or error (human-readable) screen.
  const confirmCashSettle = async () => {
    if (cashFlow === null || cashFlow.phase !== "confirm") return;
    const { amount, isTopUp: topUp, before } = cashFlow;
    setCashFlow({ phase: "processing", amount, isTopUp: topUp, before });
    try {
      await performSettle(amount, "cash");
      // Reset the calculator + refresh the live balance behind the success
      // screen (Bug 2: amount back to 0, checkboxes cleared).
      setSelectedTxIds([]);
      setCustomAmount(null);
      setRefreshTick((n) => n + 1);
      onSettled?.();
      setCashFlow({
        phase: "success",
        amount,
        before,
        after: before + amount,
      });
    } catch (e) {
      const { title, detail } = humanizeSettleError(e);
      setRefreshTick((n) => n + 1);
      setCashFlow({ phase: "error", amount, title, detail });
    }
  };

  const handleCloseAccount = async () => {
    if (selSid === null) return;
    setSettling(true);
    setError(null);
    try {
      await api.disableUser(selSid);
      setSelectedTxIds([]);
      setRefreshTick((n) => n + 1);
      onSettled?.();
    } catch (e) {
      if (e instanceof ApiError) setError(e.message);
    } finally {
      setSettling(false);
    }
  };

  const openCustom = () => {
    // Prefill with the current settle amount (in CENTS) for quick tweaks.
    setCustomInput(settleAmount > 0 ? settleAmount : null);
    // No debt → ANY amount is a top-up, so gate the input behind a confirm.
    // With a debt, entering up to it is still settling → go straight in (the
    // over-the-debt case is warned passively next to the field).
    setCustomGatePassed(absDebt > 0);
    setCustomOpen(true);
  };
  // Pass the top-up gate → reveal the input and focus it (the field mounts only
  // now, so afterOpenChange won't have focused it).
  const passCustomGate = () => {
    setCustomGatePassed(true);
    window.setTimeout(() => {
      const el = document.querySelector<HTMLInputElement>(
        ".custom-amt-wrap input",
      );
      el?.focus();
      el?.select();
    }, 50);
  };
  const applyCustom = () => {
    if (customInput == null || !(customInput > 0)) {
      msgApi.error("Enter an amount greater than 0.");
      return;
    }
    setCustomAmount(Math.round(customInput)); // already in cents
    setSelectedTxIds([]); // the purchase checklist is calculation-only — clear it
    setCustomOpen(false);
  };

  const openConvert = () => {
    setConvertName("");
    setConvertRealName("");
    setConvertPublicNote("");
    setConvertInternalNote("");
    setConvertOpen(true);
  };

  // Convert a voucher holder into a guest: flip the admin user_type via the
  // public convert endpoint (works regardless of allow_convert), then set the
  // operator-entered notes. Mirrors the patched kiosk's voucher→guest flow.
  const handleConvert = async () => {
    if (selSid === null) return;
    const display = convertName.trim();
    if (!display) {
      msgApi.error("Enter a name.");
      return;
    }
    const realName = convertRealName.trim();
    setConvertBusy(true);
    try {
      // The convert renames the strichliste user to its real_name param — pass
      // the DISPLAY name so the kiosk shows it.
      const r = await api.voucherConvertPublic(selSid, display);
      if (!r.ok) {
        msgApi.error(r.error ?? "Convert failed.");
        return;
      }
      // Audit line + the operator's contact note appended beneath it.
      const note = convertInternalNote.trim();
      const internalNote =
        `Converted from voucher by ${operatorUsername}` +
        (note ? `\n${note}` : "");
      const patch: {
        real_name?: string;
        public_note?: string;
        internal_note: string;
      } = { internal_note: internalNote };
      // real_name is an admin-only field (NOT on the strichliste) — only override
      // it when the operator typed one (else convert left it = the display name).
      if (realName) patch.real_name = realName;
      if (convertPublicNote.trim()) patch.public_note = convertPublicNote.trim();
      await api.updateUser(selSid, patch);
      setConvertOpen(false);
      setRefreshTick((n) => n + 1);
      onSettled?.();
      msgApi.success(`Converted ${display} to a guest.`);
    } catch (e) {
      msgApi.error(e instanceof ApiError ? e.message : String(e));
    } finally {
      setConvertBusy(false);
    }
  };

  const columns: ColumnsType<TransactionRow> = [
    {
      title: "When",
      dataIndex: "created",
      width: 110,
      render: (v: string) => dayjs(v).format("DD.MM HH:mm"),
    },
    {
      title: "Detail",
      key: "detail",
      render: (_: unknown, row) => renderTxDetail(row),
    },
    {
      title: "Amount",
      dataIndex: "amount",
      width: 100,
      align: "right",
      render: (v: number) => fmtCents(Math.abs(v)),
    },
  ];

  const balanceLine = (() => {
    if (selUser === null) return null;
    if (balance === null) return "Balance unknown.";
    if (balance < 0)
      return (
        <>
          Owes <Text strong>{fmtCents(absDebt)}</Text> — select purchases up to
          that amount.
        </>
      );
    if (balance > 0)
      return (
        <>
          In credit ({fmtCents(balance)}) — nothing owed. Set a custom amount to
          top them up further.
        </>
      );
    return "Balance is €0 — set a custom amount to top them up.";
  })();

  return (
    <Card
      size="small"
      title="Settle from purchases / quick buy (temporary)"
      style={{ marginBottom: 16 }}
      styles={{ body: { padding: isMobile ? 8 : undefined } }}
    >
      {msgHolder}
      {error && (
        <Alert
          type="error"
          message={error}
          closable
          onClose={() => setError(null)}
          style={{ marginBottom: 12 }}
        />
      )}

      {/* Always-present user picker (full width, own line) + bookmark bar */}
      <Select<number>
        showSearch
        allowClear
        filterOption={false}
        placeholder="Search a user…"
        searchValue={userQuery}
        onSearch={setUserQuery}
        // Reset the search the moment it's opened (clicked) → a fresh list, not
        // a stale partial result from last time.
        onDropdownVisibleChange={(open) => {
          if (open) setUserQuery("");
        }}
        loading={usersLoading}
        notFoundContent={usersLoading ? "Searching…" : "No matching users"}
        value={selSid ?? undefined}
        onChange={(v) => {
          setSelSid(v ?? null);
          setUserQuery("");
        }}
        options={userOptions}
        size="large"
        style={{
          width: "100%",
          marginBottom: 12,
          boxShadow: "0 0 0 3px rgba(145, 202, 255, 0.85)",
          borderRadius: 8,
        }}
      />
      <DndProvider backend={MultiBackend} options={HTML5toTouch}>
        <Space wrap size={[8, 8]} align="center" style={{ marginBottom: 16 }}>
          {bookmarks.map((b, i) => (
            <BookmarkChip
              key={b.sid}
              index={i}
              bookmark={b}
              active={selSid === b.sid}
              onSelect={setSelSid}
              onEdit={openPicker}
              onDelete={deleteBookmark}
              onMove={moveBookmark}
            />
          ))}
          <Button
            size="large"
            icon={<PlusOutlined />}
            title={
              selSid === null
                ? "Select a user above first"
                : "Bookmark the current user"
            }
            disabled={selSid === null}
            onClick={bookmarkCurrent}
          />
        </Space>
      </DndProvider>

      <Modal
        open={pickerOpen}
        title={
          pickerEditIndex === null
            ? "Bookmark a user"
            : "Change the bookmarked user"
        }
        footer={null}
        onCancel={() => setPickerOpen(false)}
        destroyOnClose
      >
        <Select<number>
          showSearch
          autoFocus
          filterOption={false}
          placeholder="Search a user by name…"
          searchValue={userQuery}
          onSearch={setUserQuery}
          onDropdownVisibleChange={(open) => {
            if (open) setUserQuery("");
          }}
          loading={usersLoading}
          notFoundContent={usersLoading ? "Searching…" : "No matching users"}
          onChange={(v, opt) =>
            pickUser(v, (opt as { name?: string } | undefined)?.name)
          }
          options={userOptions}
          style={{ width: "100%" }}
        />
      </Modal>

      {/* Full-screen SumUp card payment — reuses the shared reader/QR workflow.
          Closing (✕) abandons it; reopening arms a FRESH payment (destroyOnClose
          remounts the component each open). */}
      <Modal
        open={cardModalOpen}
        onCancel={() => {
          setCardModalOpen(false);
          setCardSuccess(null);
        }}
        footer={null}
        destroyOnClose
        width="100%"
        style={{ top: 0, maxWidth: "100%", paddingBottom: 0 }}
        styles={{ content: { minHeight: "100vh", borderRadius: 0 } }}
        closeIcon={<CloseOutlined style={{ fontSize: 28 }} />}
        title={`Card payment — ${selUser?.name ?? ""}`}
      >
        {cardSuccess ? (
          // Settled → the shared "everything worked" screen (balance flow).
          <SettleSuccessView
            before={cardSuccess.before}
            added={cardSuccess.added}
            after={cardSuccess.after}
            method="card"
            onDone={() => {
              setCardModalOpen(false);
              setCardSuccess(null);
            }}
          />
        ) : (
          selSid !== null &&
          cardAmount !== null && (
            <SumupReaderSettle
              strichlisteUserId={selSid}
              amountCents={cardAmount}
              onSettled={() => {
                setSelectedTxIds([]);
                setCustomAmount(null);
                setRefreshTick((n) => n + 1);
                onSettled?.();
                if (cardBalanceBefore !== null) {
                  setCardSuccess({
                    before: cardBalanceBefore,
                    added: cardAmount,
                    after: cardBalanceBefore + cardAmount,
                  });
                }
              }}
            />
          )
        )}
      </Modal>

      {/* Windowed CASH workflow — confirm the cash was handed over, then show a
          big green ✓ + balance flow, or a big red ✗ + a human-readable reason. */}
      <Modal
        open={cashFlow !== null}
        onCancel={() => {
          if (cashFlow?.phase === "processing") return; // don't abort mid-post
          setCashFlow(null);
        }}
        footer={null}
        destroyOnClose
        closable={cashFlow?.phase !== "processing"}
        maskClosable={cashFlow?.phase !== "processing"}
        title={null}
        width={420}
      >
        {cashFlow?.phase === "confirm" && (
          <div style={{ textAlign: "center", padding: "16px 8px" }}>
            <QuestionCircleFilled style={{ color: "#faad14", fontSize: 88 }} />
            <Title level={3} style={{ marginTop: 16, marginBottom: 8 }}>
              Cash received?
            </Title>
            <Text style={{ fontSize: 16 }}>
              Did <Text strong>{selUser?.name}</Text> hand over{" "}
              <Text strong>{fmtCents(cashFlow.amount)}</Text> in cash?
            </Text>
            {cashFlow.isTopUp && (
              <div style={{ marginTop: 10 }}>
                <Text type="warning">
                  {absDebt > 0
                    ? `${fmtCents(
                        cashFlow.amount - absDebt,
                      )} of this tops ${selUser?.name ?? "them"} up into credit.`
                    : `This tops ${
                        selUser?.name ?? "them"
                      } up into credit (they owe nothing).`}
                </Text>
              </div>
            )}
            <Button
              type="primary"
              size="large"
              block
              onClick={() => void confirmCashSettle()}
              style={{ height: 56, fontSize: 20, marginTop: 24 }}
            >
              ✓ Yes, received
            </Button>
            <Button
              size="large"
              block
              onClick={() => setCashFlow(null)}
              style={{ marginTop: 12 }}
            >
              Cancel
            </Button>
          </div>
        )}
        {cashFlow?.phase === "processing" && (
          <div style={{ textAlign: "center", padding: "48px 8px" }}>
            <Spin size="large" />
            <div style={{ marginTop: 16 }}>
              <Text type="secondary">Recording payment…</Text>
            </div>
          </div>
        )}
        {cashFlow?.phase === "success" && (
          <SettleSuccessView
            before={cashFlow.before}
            added={cashFlow.amount}
            after={cashFlow.after}
            method="cash"
            onDone={() => setCashFlow(null)}
          />
        )}
        {cashFlow?.phase === "error" && (
          <div style={{ textAlign: "center", padding: "16px 8px" }}>
            <CloseCircleFilled style={{ color: "#ff4d4f", fontSize: 88 }} />
            <Title level={3} style={{ marginTop: 16, marginBottom: 8 }}>
              {cashFlow.title}
            </Title>
            <Text style={{ fontSize: 15 }}>{cashFlow.detail}</Text>
            <Button
              size="large"
              block
              onClick={() => setCashFlow(null)}
              style={{ marginTop: 24 }}
            >
              Close
            </Button>
          </div>
        )}
      </Modal>

      {/* Custom amount to pay — overrides the checked-purchase sum. For a
          no-debt user the input is GATED behind a top-up confirmation. */}
      <Modal
        open={customOpen}
        onCancel={() => setCustomOpen(false)}
        title={customGatePassed ? "Custom amount to pay" : "Top-up, not settlement"}
        okText="Set amount"
        onOk={applyCustom}
        footer={customGatePassed ? undefined : null}
        destroyOnClose
        wrapClassName="custom-amt-wrap"
        // autoFocus on a modal input doesn't reliably grab focus on mobile;
        // focus + select once the modal has finished opening (entry phase only).
        afterOpenChange={(open) => {
          if (!open || !customGatePassed) return;
          const el = document.querySelector<HTMLInputElement>(
            ".custom-amt-wrap input",
          );
          if (el) {
            el.focus();
            el.select();
          }
        }}
      >
        {!customGatePassed ? (
          // GATE — must be confirmed before any amount can be entered.
          <div style={{ textAlign: "center", padding: "8px 4px" }}>
            <WarningFilled style={{ color: "#faad14", fontSize: 64 }} />
            <Title level={4} style={{ marginTop: 12, marginBottom: 8 }}>
              This is a top-up, not a settlement
            </Title>
            <Text>
              <Text strong>{selUser?.name}</Text> owes nothing — whatever you
              enter next will <Text strong>top them up into credit</Text>, not
              settle a debt.
            </Text>
            <Button
              type="primary"
              size="large"
              block
              onClick={passCustomGate}
              style={{ marginTop: 24 }}
            >
              Continue to top-up
            </Button>
            <Button
              size="large"
              block
              onClick={() => setCustomOpen(false)}
              style={{ marginTop: 12 }}
            >
              Cancel
            </Button>
          </div>
        ) : (
          <Space direction="vertical" size="middle" style={{ width: "100%" }}>
          <NumInput<number>
            autoFocus
            size="large"
            min={0}
            step={50}
            precision={0}
            addonAfter="ct"
            placeholder="e.g. 1000 = 10,00 €"
            style={{
              width: "100%",
              fontSize: isMobile ? 44 : 52,
              fontWeight: 600,
            }}
            value={customInput ?? undefined}
            onChange={(v) => setCustomInput(v ?? null)}
            onPressEnter={applyCustom}
            // Highlight the prefilled value on focus so typing overwrites it
            // (AntD may reposition the cursor on focus → select on the next tick).
            onFocus={(e) => {
              const el = e.target;
              window.setTimeout(() => el.select(), 0);
            }}
          />
          {/* Pre-gate: the moment the entered value passes the debt, this stops
              being a settlement and becomes a top-up into credit. Warn before
              the operator even reaches the confirm window. */}
          {customInput != null && customInput > absDebt && (
            <Alert
              type="warning"
              showIcon
              message="This is a top-up, not a settlement"
              description={
                absDebt > 0
                  ? `${selUser?.name ?? "They"} owe ${fmtCents(
                      absDebt,
                    )} — that part settles the debt, and the extra ${fmtCents(
                      customInput - absDebt,
                    )} puts them into credit.`
                  : `${selUser?.name ?? "They"} owe nothing — the full ${fmtCents(
                      customInput,
                    )} goes onto their balance as credit.`
              }
            />
          )}
          <Text type="secondary" style={{ fontSize: 12 }}>
            In cents (1000 = 10,00 €). Overrides the checked purchases (the
            checklist is for calculation only). Up to what's owed (
            {fmtCents(absDebt)}) settles the debt; anything beyond tops the user
            up into credit. Cash has no minimum, card needs{" "}
            {fmtCents(MIN_CHARGE_CENTS)}.
          </Text>
          </Space>
        )}
      </Modal>

      {/* Convert a (non-convertible) voucher holder into a guest. */}
      <Modal
        open={convertOpen}
        onCancel={() => setConvertOpen(false)}
        title="Convert voucher to guest"
        okText="Convert to Guest"
        confirmLoading={convertBusy}
        onOk={() => void handleConvert()}
      >
        <Space direction="vertical" size="middle" style={{ width: "100%" }}>
          <div>
            <Text type="secondary">Name</Text>
            <Input
              value={convertName}
              onChange={(e) => setConvertName(e.target.value)}
              placeholder="Tab / display name"
            />
            <Text type="secondary" style={{ fontSize: 11 }}>
              Shown on the kiosk.
            </Text>
          </div>
          <div>
            <Text type="secondary">Real name</Text>
            <Input
              value={convertRealName}
              onChange={(e) => setConvertRealName(e.target.value)}
              placeholder="Who's responsible (optional)"
            />
            <Text type="secondary" style={{ fontSize: 11 }}>
              Admin only — not shown on the strichliste.
            </Text>
          </div>
          <div>
            <Text type="secondary">Public note</Text>
            <Input.TextArea
              rows={2}
              value={convertPublicNote}
              onChange={(e) => setConvertPublicNote(e.target.value)}
            />
          </div>
          <div>
            <Text type="secondary">Internal note</Text>
            <Input.TextArea
              rows={2}
              value={convertInternalNote}
              onChange={(e) => setConvertInternalNote(e.target.value)}
              placeholder="Contact info — name / crew / phone"
            />
            <Text type="secondary" style={{ fontSize: 11 }}>
              Admin only — saved as “Converted from voucher by {operatorUsername}”
              + this note.
            </Text>
          </div>
        </Space>
      </Modal>

      {selSid === null ? (
        <Text type="secondary">
          Add or pick a user above to settle purchases or buy articles for them.
        </Text>
      ) : (
        <Row gutter={isMobile ? [0, 8] : 16} align="stretch">
          {/* Settle pane — desktop LEFT (order 1); on mobile it drops BELOW the
              articles (order 2) so the buy pane sits between the user header and
              the purchase history. */}
          <Col
            xs={{ span: 24, order: 2 }}
            lg={{ span: 12, order: 1 }}
            style={{ marginBottom: 16 }}
          >
            <Card
              size="small"
              title="Settle from purchases"
              bordered={!isMobile}
              style={{ height: "100%" }}
              styles={{ body: { padding: isMobile ? 0 : undefined } }}
            >
              <Space direction="vertical" size="middle" style={{ width: "100%" }}>
                <Text type="secondary">{balanceLine}</Text>
                <Table<TransactionRow>
                  // Remount per user so the scroll body resets to the top.
                  key={selSid ?? "none"}
                  size="small"
                  rowKey="id"
                  loading={txLoading}
                  columns={columns}
                  dataSource={recentTx}
                  pagination={false}
                  scroll={{ y: 240 }}
                  rowSelection={{
                    selectedRowKeys: selectedTxIds,
                    onChange: (keys) => setSelectedTxIds(keys as number[]),
                    // Only article purchases are settleable; tick those.
                    getCheckboxProps: (row) => ({ disabled: !isArticleBuy(row) }),
                  }}
                  // Touch-friendly: tapping anywhere on an article row toggles it.
                  onRow={(row) => ({
                    // Article purchases are the actionable rows; dim the rest
                    // (settlements / vouchers / deposits) since they're context.
                    style: isArticleBuy(row)
                      ? { cursor: "pointer" }
                      : { opacity: 0.45 },
                    onClick: (e) => {
                      if (!isArticleBuy(row)) return;
                      // The checkbox cell already toggles — don't double-toggle.
                      if (
                        (e.target as HTMLElement).closest(
                          ".ant-table-selection-column",
                        )
                      )
                        return;
                      setSelectedTxIds((prev) =>
                        prev.includes(row.id)
                          ? prev.filter((id) => id !== row.id)
                          : [...prev, row.id],
                      );
                    },
                  })}
                  locale={{ emptyText: "No recent transactions." }}
                />
                <Title level={2} style={{ margin: 0 }}>
                  {selUser?.name ?? "—"}
                </Title>
                {voucherInfo?.isVoucher && (
                  <Button onClick={openConvert} style={{ alignSelf: "flex-start" }}>
                    Convert to Guest
                  </Button>
                )}
                <div style={{ display: "flex", alignItems: "stretch", gap: 16 }}>
                  {/* Big amount on the left, ~the height of the stacked buttons.
                      Click it (or the link) to enter a custom amount to pay. */}
                  <div
                    style={{
                      flex: 1,
                      display: "flex",
                      flexDirection: "column",
                      justifyContent: "center",
                      minWidth: 0,
                    }}
                  >
                    <Text
                      strong
                      onClick={openCustom}
                      style={{
                        fontSize: isMobile ? 44 : 56,
                        lineHeight: 1,
                        cursor: "pointer",
                      }}
                    >
                      {fmtCents(settleAmount)}
                    </Text>
                    <a onClick={openCustom} style={{ fontSize: 12 }}>
                      {customAmount !== null
                        ? "✎ custom amount (tap to change)"
                        : "✎ set custom amount"}
                    </a>
                  </div>
                  {/* Two pay buttons stacked + right-aligned; emoji over label */}
                  <Space size={8} style={{ flexShrink: 0 }}>
                    <Button
                      type="primary"
                      disabled={!canSettleBase || !cashAvailable}
                      onClick={openCashConfirm}
                      style={{ height: 64, width: 88, padding: 4 }}
                    >
                      <span
                        style={{
                          display: "block",
                          lineHeight: 1.1,
                          textAlign: "center",
                        }}
                      >
                        <span style={{ fontSize: 28 }}>💵</span>
                        <br />
                        CASH
                      </span>
                    </Button>
                    {/* CARD only when SumUp is on (hidden once we know it's
                        off, so there's no dead button that opens then fails). */}
                    {sumupEnabled !== false && (
                      <Button
                        type="primary"
                        ghost
                        disabled={!canSettleBase || belowCardMin}
                        onClick={() => {
                          setCardAmount(settleAmount);
                          setCardBalanceBefore(balance);
                          setCardSuccess(null);
                          setCardModalOpen(true);
                        }}
                        style={{ height: 64, width: 88, padding: 4 }}
                      >
                        <span
                          style={{
                            display: "block",
                            lineHeight: 1.1,
                            textAlign: "center",
                          }}
                        >
                          <span style={{ fontSize: 28 }}>💳</span>
                          <br />
                          CARD
                        </span>
                      </Button>
                    )}
                  </Space>
                </div>
                {capped && (
                  <Text type="secondary">
                    Capped to {fmtCents(absDebt)} — the remaining balance.
                  </Text>
                )}
                {isTopUp && (
                  <Text type="warning">
                    {absDebt > 0 ? (
                      <>
                        {fmtCents(settleAmount - absDebt)} above the debt — tops{" "}
                        {selUser?.name} up into credit (confirmed on settle).
                      </>
                    ) : (
                      <>
                        Tops {selUser?.name} up {fmtCents(settleAmount)} into
                        credit (confirmed on settle).
                      </>
                    )}
                  </Text>
                )}
                {belowCardMin && (
                  <Text type="secondary">
                    Card needs a {fmtCents(MIN_CHARGE_CENTS)} minimum — cash is
                    fine.
                  </Text>
                )}
                <div style={{ textAlign: "right" }}>
                  <Popconfirm
                    title="Close this account?"
                    description="The account will be disabled. Settle any remaining balance first."
                    okText="Close account"
                    okButtonProps={{ danger: true }}
                    onConfirm={() => void handleCloseAccount()}
                  >
                    <Button danger size="small" loading={settling}>
                      Close account
                    </Button>
                  </Popconfirm>
                </div>
              </Space>
            </Card>
          </Col>

          {/* Buy pane — desktop RIGHT (order 2); FIRST on mobile (order 1). */}
          <Col
            xs={{ span: 24, order: 1 }}
            lg={{ span: 12, order: 2 }}
            style={{ marginBottom: 16 }}
          >
            <ArticleBuyPane
              strichlisteUserId={selSid}
              userName={selUser?.name ?? null}
              onBought={(txId) => {
                if (typeof txId === "number") setPendingSelect(txId);
                setRefreshTick((n) => n + 1);
              }}
              onSelectUser={(sid) => setSelSid(sid)}
            />
          </Col>
        </Row>
      )}
    </Card>
  );
}
